"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { NavLink } from "react-router-dom"
import { AmyPillButton } from "@/components/ui/amy-pill-button"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/hooks/useAuth"
import { useI18n } from "@/i18n"
import { BrandMarkV2 } from "@/components/landing/BrandMarkV2"
import { navItems } from "./navItems"
import { LanguageToggle } from "./LanguageToggle"

interface LeftRailProps {
  onOpenAmy: () => void
}

/** Desktop (>=768px) left app rail: six areas (Pantry promoted 2026-08) + Amy toggle, per ui-ux-spec.md. */
export function LeftRail({ onOpenAmy }: LeftRailProps) {
  const { t } = useI18n()
  const { email, signOut } = useAuth()

  // sticky + h-screen (owner fix 2026-08-28): as a flex child the rail
  // stretched to the FULL PAGE height, so justify-between pushed the
  // language/email/sign-out cluster to the bottom of a possibly very long
  // page — users had to scroll to find Sign out. Pinning the rail to the
  // viewport keeps that cluster always visible; if the rail's own content
  // ever exceeds the viewport it scrolls internally.
  return (
    <nav
      aria-label={t("nav.primaryNavigation")}
      className="hidden shrink-0 flex-col justify-between border-r border-line bg-paper px-4 py-6 md:sticky md:top-0 md:flex md:h-screen md:w-60 md:self-start md:overflow-y-auto"
    >
      <div className="flex flex-col gap-1">
        <span className="mb-4 flex items-center gap-2 px-2 text-lg font-bold text-ink">
          <BrandMarkV2 size={24} />
          {t("common.appName")}
        </span>
        {navItems.map(({ to, labelKey, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex min-h-11 items-center gap-3 rounded-bb-md px-3 text-sm font-medium ${
                isActive ? "bg-green text-paper" : "text-ink-2 hover:bg-cream"
              }`
            }
          >
            <Icon className="size-5" aria-hidden="true" />
            {t(labelKey)}
          </NavLink>
        ))}
        <AmyPillButton
          label={t("amy.askAmy")}
          onClick={onOpenAmy}
          className="mt-4 w-full justify-center"
        />
      </div>

      <div className="flex flex-col gap-3">
        <LanguageToggle />
        {email && <p className="truncate px-2 text-xs text-muted-foreground">{email}</p>}
        <Button
          type="button"
          variant="ghost"
          className="min-h-11 justify-start"
          onClick={() => void signOut()}
        >
          {t("nav.signOut")}
        </Button>
      </div>
    </nav>
  )
}
